import {
  forcedImageContentTypeForKey,
  isImageCdnKey,
} from "@hightouch/lib/ad-studio/asset-content-type";
import { looksLikeHtml } from "@hightouch/lib/ad-studio/html-editor/looks-like-html";
import {
  type AdRefImageHints,
  resolveAdRefs,
} from "@hightouch/lib/ad-studio/html-editor/resolve-ad-refs";
import { convertSvgToHtml } from "@hightouch/lib/ad-studio/svg-to-html";

import { scopeSvgIds } from "./scope-svg-ids";

export interface PreparedHtml {
  /** HTML the renderer can drop into a `dangerouslySetInnerHTML` wrapper. */
  html: string;
  /** Natural canvas dimensions read from `style="width:Wpx; height:Hpx"`. */
  dimensions: { width: number; height: number } | null;
  /**
   * `convertSvgToHtml`'s skipped-construct report when the content needed
   * conversion (empty for HTML input). Most entries (`text`, `path`, `svg`,
   * …) mean "preserved as an opaque svg layer — renders right, not deeply
   * editable"; `mask` / `filter` / `clip-path` mean the effect was DROPPED
   * and the converted markup renders differently from the source.
   */
  skipped: string[];
}

/**
 * Normalize any saved ad content into HTML the renderer can paint directly.
 * Static ads now save HTML; legacy / motion-source ads saved SVG. This is
 * the single boundary where SVG → HTML conversion happens, so every
 * downstream consumer (editor mount, thumbnails, size variants, capture)
 * only deals with HTML.
 *
 * Conversion fidelity caveat: SVG-native primitives (`<path>`, `<circle>`,
 * gradient-filled text, …) are preserved verbatim inside inline-`<svg>`
 * fallback layers — they render correctly but aren't individually editable —
 * while `mask`/`filter` effects are dropped outright. See `skipped` on the
 * result. Simple `<text>`/`<tspan>` and `<foreignObject>` text convert to
 * real editable text layers. If you need pixel-perfect SVG fidelity, fall
 * back to the rasterized `asset_url` PNG.
 *
 * Returns `null` when the input can't be parsed (so callers can cleanly
 * fall back to image rendering).
 */
export function prepareAdContentForDisplay(
  content: string,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string,
  imageHints?: AdRefImageHints,
  /** Scales SVG conversion into this box (see `ConvertSvgToHtmlOptions`);
   *  HTML input is already in its own declared space and is never scaled. */
  targetSize?: { width: number; height: number },
  /** Scope SVG references for this mounted preview; omit when initializing editable content. */
  svgIdPrefix?: string,
): PreparedHtml | null {
  if (!content) return null;
  try {
    let html: string;
    let skipped: string[] = [];
    if (looksLikeHtml(content)) {
      html = content;
    } else {
      const converted = convertSvgToHtml(content, { targetSize });
      html = converted.html;
      skipped = converted.skipped;
    }
    const prepared = prepareHtml(
      html,
      componentAssets,
      apiBase,
      imageHints,
      svgIdPrefix,
    );
    return prepared ? { ...prepared, skipped } : null;
  } catch {
    return null;
  }
}

function prepareHtml(
  html: string,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string,
  imageHints: AdRefImageHints | undefined,
  svgIdPrefix: string | undefined,
): Omit<PreparedHtml, "skipped"> | null {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const root = doc.body.firstElementChild;
  if (!(root instanceof HTMLElement)) return null;
  resolveAdRefs(root, componentAssets, apiBase, imageHints);
  streamRawSvgImages(root);
  const w = Number.parseFloat(root.style.width);
  const h = Number.parseFloat(root.style.height);
  const dimensions =
    Number.isFinite(w) && Number.isFinite(h) ? { width: w, height: h } : null;
  if (svgIdPrefix !== undefined) scopeSvgIds(doc.body, svgIdPrefix);
  return { html: root.outerHTML, dimensions };
}

/** Resolve assets while preserving sibling styles and arbitrary HTML roots. */
export function prepareHtmlMarkupForDisplay(
  html: string,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string,
  imageHints?: AdRefImageHints,
): string {
  const doc = new DOMParser().parseFromString(
    `<!DOCTYPE html><body><div data-template-preview-root>${html}</div></body>`,
    "text/html",
  );
  const root = doc.body.querySelector<HTMLElement>(
    "[data-template-preview-root]",
  );
  if (!root) return html;
  resolveAdRefs(root, componentAssets, apiBase, imageHints);
  streamRawSvgImages(root);
  return root.innerHTML;
}

/**
 * Route raw-served SVG `<img>`s through the API's streaming endpoint so it sets
 * `Content-Type: image/svg+xml` from the extension. Agent-uploaded ad-asset
 * SVGs are stored as `application/octet-stream` (their presigned PUT can't sign
 * a Content-Type), and a browser won't paint a non-`image/svg+xml` SVG in an
 * `<img>`. That's why a logo can show in the imgproxy-rasterized grid thumbnail
 * but go blank on the editor canvas, which serves the raw object via a redirect.
 * Resized requests (the thumbnail's `width`/`height`/`format`) come back as a
 * real image format already, so they're left untouched.
 */
function streamRawSvgImages(root: HTMLElement): void {
  for (const img of Array.from(root.querySelectorAll("img"))) {
    const src = img.getAttribute("src");
    if (!src) continue;
    const streamed = withStreamParamForRawSvg(src);
    if (streamed) img.setAttribute("src", streamed);
  }
}

function withStreamParamForRawSvg(src: string): string | null {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }
  if (!url.pathname.endsWith("/agents/files")) return null;
  const key = url.searchParams.get("key");
  if (!key || !forcedImageContentTypeForKey(key)) return null;
  // A resize was requested → imgproxy returns a rasterized format that renders
  // fine; only the raw-served path needs the API to set the SVG content-type.
  if (
    url.searchParams.has("format") ||
    url.searchParams.has("width") ||
    url.searchParams.has("height") ||
    url.searchParams.has("stream")
  ) {
    return null;
  }
  url.searchParams.set("stream", "true");
  return url.toString();
}

/**
 * A web page's stored HTML with its `ad:` refs resolved and every other load blocked, ready for an iframe.
 * Not `prepareAdContentForDisplay`, which keeps only the body's first element and drops the `<head>` stylesheet.
 */
export function prepareWebPageHtml(
  source: string,
  assets: Record<string, string>,
  apiEndpoint: string,
  options: {
    /** Loads raster assets through the resizer at this width, for a preview drawn far smaller than the page. */
    imageWidth?: number;
  } = {},
): string {
  const doc = new DOMParser().parseFromString(source, "text/html");
  const loadUrls = options.imageWidth
    ? resizedRasterAssets(assets, apiEndpoint, options.imageWidth)
    : assets;
  // URLs stand in for S3 keys: resolveAdRefs only resolves refs that have a key.
  resolveAdRefs(doc.documentElement, assets, apiEndpoint, {
    targetRenderer: "hightouch-webapp",
    directUrls: loadUrls,
  });
  streamRawSvgImages(doc.documentElement);
  // The server rejects these too; a refresh could navigate the preview and a second CSP would compete with ours.
  for (const meta of Array.from(doc.querySelectorAll("meta[http-equiv]"))) {
    const value = meta.getAttribute("http-equiv")?.trim().toLowerCase();
    if (value === "refresh" || value === "content-security-policy") {
      meta.remove();
    }
  }
  const csp = doc.createElement("meta");
  csp.httpEquiv = "Content-Security-Policy";
  csp.content = blockExternalLoadsPolicy(
    [apiEndpoint, ...Object.values(assets)],
    // The resizer answers from a CDN host the app doesn't know ahead of time; naming it here would replace `https:`.
    { allowHttpsImages: Boolean(options.imageWidth) },
  );
  doc.head.prepend(csp);
  return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
}

// EmailPreviewIframe's CSP only blocks scripts; this blocks every other load too, backing up the server's external URL check.
function blockExternalLoadsPolicy(
  assetUrls: string[],
  { allowHttpsImages = false }: { allowHttpsImages?: boolean } = {},
): string {
  const origins = new Set<string>();
  for (const url of assetUrls) {
    try {
      origins.add(new URL(url).origin);
    } catch {
      // An unparseable URL can't load, so it needs no allowance.
    }
  }
  return [
    "default-src 'none'",
    `img-src data: ${allowHttpsImages ? "https: " : ""}${[...origins].join(" ")}`,
    "style-src 'unsafe-inline'",
    `font-src data: ${[...origins].join(" ")}`,
    "form-action 'none'",
    "base-uri 'none'",
  ].join("; ");
}

/** Each raster asset as a resized `/agents/files` URL; SVGs and URLs that carry no storage key load as they are. */
function resizedRasterAssets(
  assets: Record<string, string>,
  apiEndpoint: string,
  width: number,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(assets).map(([name, url]) => {
      const key = assetStorageKey(url);
      if (!key || !isImageCdnKey(key)) return [name, url];
      const params = new URLSearchParams({
        key,
        width: String(Math.round(width)),
        format: "webp",
      });
      return [name, `${apiEndpoint}/agents/files?${params.toString()}`];
    }),
  );
}

/**
 * The storage key behind an asset URL: the `key` of an `/agents/files` URL, or the object path of a signed S3 URL.
 * Read from the URL only until the API hands web page asset keys to the client.
 */
export function assetStorageKey(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.pathname.endsWith("/agents/files")) {
    return parsed.searchParams.get("key");
  }
  if (!parsed.hostname.endsWith(".amazonaws.com")) return null;
  const path = decodeURIComponent(parsed.pathname.slice(1));
  // A path-style URL (s3.<region>.amazonaws.com/<bucket>/<key>) leads with its bucket.
  const isPathStyle = parsed.hostname.startsWith("s3.");
  const key = isPathStyle ? path.slice(path.indexOf("/") + 1) : path;
  return key || null;
}
